/**
 * combined-swap.js — in-place product swap for the Combined Products picker.
 *
 * When a <combined-products-picker> option is clicked, fetch the sibling product's page, replace the
 * current product section's markup with the incoming one, and re-run Prestige's section framework so
 * the buy form, gallery, price and inventory re-initialize against the new product — no full reload.
 *
 * The picker renders plain <a href> links, so the feature degrades to normal navigation with JS off,
 * on modified clicks (new tab), or if anything goes wrong during the swap.
 */
(function () {
  'use strict';

  // Idempotent: the picker snippet ships a <script src="combined-swap.js"> inside the product
  // section, so an in-place swap re-injects it. Guard so the delegated click handler binds once.
  if (window.__combinedSwapLoaded) return;
  window.__combinedSwapLoaded = true;

  var SECTION_PREFIX = 'shopify-section-';
  var boundPopstate = false;

  function dispatchSectionEvent(name, sectionEl, sectionId) {
    sectionEl.dispatchEvent(
      new CustomEvent(name, { bubbles: true, detail: { sectionId: sectionId } })
    );
  }

  // Re-initialize the product gallery after a swap. The theme's gallery script (new-main-product.js)
  // creates its Swiper instances at module load and declares module-scoped globals, so it can't be
  // re-run (the globals redeclare and throw) and it doesn't listen for section events. Rebuild just
  // the two Swipers against the swapped-in markup, restoring the theme's deferred images first.
  function reinitGallery(container) {
    // Restore images the theme deferred as transparent SVGs (its iOS-memory optimization) so the
    // swapped-in slides carry real URLs again.
    container.querySelectorAll('img[data-defer-src]').forEach(function (img) {
      var src = img.getAttribute('data-defer-src');
      var srcset = img.getAttribute('data-defer-srcset');
      var sizes = img.getAttribute('data-defer-sizes');
      if (src) img.setAttribute('src', src);
      if (srcset) img.setAttribute('srcset', srcset);
      if (sizes) img.setAttribute('sizes', sizes);
      img.removeAttribute('data-defer-src');
      img.removeAttribute('data-defer-srcset');
      img.removeAttribute('data-defer-sizes');
    });

    if (typeof window.Swiper !== 'function') return;

    var mainEl = container.querySelector('.image-main');
    if (!mainEl) return;
    var thumbsEl = container.querySelector('.mySwiper');
    var isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;

    var thumbs = thumbsEl
      ? new window.Swiper(thumbsEl, {
          direction: 'vertical',
          slidesPerView: 4,
          navigation: { nextEl: '.swiper-button-next', prevEl: '.swiper-button-prev' },
        })
      : null;

    new window.Swiper(mainEl, {
      loop: !isMobile,
      spaceBetween: 0,
      slidesPerView: 1,
      noSwipingSelector: 'video',
      thumbs: thumbs ? { swiper: thumbs } : undefined,
      pagination: { el: '.swiper-pagination', clickable: true, dynamicBullets: true, dynamicMainBullets: 8 },
    });

    // The theme reveals lazy images by swapping a class once lazysizes/DOMContentLoaded fires — but
    // neither re-runs after a swap, so the images keep their opacity:0 loading state under the swiper
    // preloader. Force the swapped-in gallery images visible and drop the leftover loading overlays.
    container.querySelectorAll('.image-main img, .mySwiper img, .content-thumbs-Slider img').forEach(function (img) {
      img.classList.add('lazyloaded');
      img.classList.remove('lazyload');
      img.style.opacity = '1';
      // The theme shows a spinner via `.HydrateSlide.is-loading` and clears it in an image load
      // handler that never re-binds after a swap. Clear it here once the image is ready.
      var clearSpinner = function () {
        var slide = img.closest('.HydrateSlide');
        if (slide) slide.classList.remove('is-loading');
      };
      if (img.complete && img.naturalWidth > 0) clearSpinner();
      else img.addEventListener('load', clearSpinner, { once: true });
    });
    container.querySelectorAll('.swiper-lazy-preloader, .Image__Loader').forEach(function (el) {
      el.remove();
    });
  }

  function swap(url, sectionId, owner) {
    if (!url || !sectionId) {
      if (url) window.location.href = url;
      return;
    }

    if (owner) owner.classList.add('is-loading');

    fetch(url, { headers: { Accept: 'text/html' } })
      .then(function (res) {
        if (!res.ok) throw new Error('Fetch failed: ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var incoming = doc.getElementById(SECTION_PREFIX + sectionId);
        var current = document.getElementById(SECTION_PREFIX + sectionId);

        if (!incoming || !current) {
          window.location.href = url;
          return;
        }

        // Tear down the outgoing section's JS, replace its markup, then re-init the incoming one.
        // Prestige's section framework binds to Shopify's editor lifecycle events, so reusing them
        // re-runs the product form, gallery and price logic against the swapped-in markup.
        dispatchSectionEvent('shopify:section:unload', current, sectionId);
        current.innerHTML = incoming.innerHTML;
        document.title = doc.title;
        history.pushState({ combinedSwap: true, url: url }, '', url);

        // Rebuild the gallery Swipers against the new markup, then fire the lifecycle event so the
        // theme framework re-initializes the rest of the product section (form, price, etc.).
        reinitGallery(current);
        dispatchSectionEvent('shopify:section:load', current, sectionId);

        // Keep the cart/UI in sync the way a normal PDP load would.
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

        if (window.lazySizes && typeof window.lazySizes.autoSizer === 'object') {
          window.lazySizes.autoSizer.checkElems();
        }

        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .catch(function (err) {
        console.error('[combined-swap]', err);
        window.location.href = url;
      })
      .finally(function () {
        if (owner) owner.classList.remove('is-loading');
      });
  }

  function bindPopstate() {
    if (boundPopstate) return;
    boundPopstate = true;
    window.addEventListener('popstate', function (event) {
      // Only reload for entries this feature created; leave unrelated history alone.
      if (event.state && event.state.combinedSwap) {
        window.location.reload();
      }
    });
  }

  function onClick(event) {
    var link = event.target.closest('.CombinedPicker__Option');
    if (!link) return;

    var picker = link.closest('combined-products-picker');
    if (!picker) return;

    if (link.classList.contains('is-current') || link.classList.contains('is-unavailable')) {
      event.preventDefault();
      return;
    }
    // Respect new-tab / modified clicks — let the browser navigate normally.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }

    event.preventDefault();
    bindPopstate();
    swap(link.getAttribute('href'), picker.getAttribute('data-section-id'), picker);
  }

  // Delegated at the document level so it survives section swaps without re-binding.
  document.addEventListener('click', onClick);
})();
